function Sk({ className }: { className: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded ${className}`} />
}

// ── Plumber list card ─────────────────────────────────────────────────────────

export function PlumberCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex gap-4">
      <Sk className="flex-shrink-0 w-14 h-14 rounded-xl" />
      <div className="flex-1 min-w-0 space-y-2.5">
        <Sk className="h-4 w-2/3" />
        <Sk className="h-3 w-1/3" />
        <div className="flex gap-2 pt-1">
          <Sk className="h-5 w-16 rounded-full" />
          <Sk className="h-5 w-20 rounded-full" />
        </div>
      </div>
    </div>
  )
}

export function PlumberCardListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => <PlumberCardSkeleton key={i} />)}
    </div>
  )
}

// ── Navigation card (county / city / neighbourhood) ───────────────────────────

export function NavCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Sk className="w-9 h-9 rounded-xl flex-shrink-0" />
        <div className="space-y-1.5">
          <Sk className="h-3.5 w-28" />
          <Sk className="h-3 w-16" />
        </div>
      </div>
      <Sk className="h-4 w-4 rounded" />
    </div>
  )
}

export function NavCardGridSkeleton({ count = 6, cols = 2 }: { count?: number; cols?: 2 | 3 }) {
  const grid = cols === 3
    ? 'grid grid-cols-2 sm:grid-cols-3 gap-3'
    : 'grid grid-cols-1 sm:grid-cols-2 gap-3'
  return (
    <div className={grid}>
      {Array.from({ length: count }).map((_, i) => <NavCardSkeleton key={i} />)}
    </div>
  )
}

// ── Postcode card (compact) ───────────────────────────────────────────────────

export function PostcodeCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Sk className="w-8 h-8 rounded-lg flex-shrink-0" />
        <div className="space-y-1.5">
          <Sk className="h-3.5 w-14" />
          <Sk className="h-3 w-10" />
        </div>
      </div>
      <Sk className="h-4 w-4 rounded" />
    </div>
  )
}

export function PostcodeCardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {Array.from({ length: count }).map((_, i) => <PostcodeCardSkeleton key={i} />)}
    </div>
  )
}

// ── Full plumber profile ──────────────────────────────────────────────────────

// Directory profile page: same width/grid as DirectoryPlumber (max-w-7xl,
// main + 300px sidebar at lg) so the skeleton → loaded swap doesn't shift layout.
export function DirectoryPlumberSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-10 lg:items-start">
        <div className="min-w-0">
          <Sk className="h-4 w-64 mb-6" />
          <PlumberProfileBlocks />
        </div>
        <div className="mt-8 lg:mt-0 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <Sk className="h-12 w-full rounded-xl" />
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex justify-between">
                <Sk className="h-4 w-20" />
                <Sk className="h-4 w-24" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function PlumberProfileSkeleton() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <PlumberProfileBlocks />
    </div>
  )
}

function PlumberProfileBlocks() {
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
        <Sk className="h-5 w-28 rounded-full" />
        <Sk className="h-7 w-1/2" />
        <Sk className="h-4 w-2/5" />
        <div className="flex gap-2 pt-1">
          <Sk className="h-4 w-4 rounded-full" />
          <Sk className="h-4 w-4 rounded-full" />
          <Sk className="h-4 w-4 rounded-full" />
          <Sk className="h-4 w-4 rounded-full" />
          <Sk className="h-4 w-4 rounded-full" />
          <Sk className="h-4 w-10 ml-1" />
        </div>
        <div className="flex gap-4 pt-1">
          <Sk className="h-4 w-28" />
          <Sk className="h-4 w-20" />
        </div>
      </div>
      {/* Bio */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-2.5">
        <Sk className="h-3 w-12 mb-3" />
        <Sk className="h-3 w-full" />
        <Sk className="h-3 w-5/6" />
        <Sk className="h-3 w-4/5" />
        <Sk className="h-3 w-3/4" />
      </div>
      {/* Qualifications */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <Sk className="h-3 w-36 mb-4" />
        <div className="flex flex-wrap gap-2">
          {[72, 56, 88, 64, 80].map((w, i) => (
            <div key={i} className="h-6 animate-pulse bg-gray-100 rounded-full" style={{ width: w }} />
          ))}
        </div>
      </div>
      {/* Hours */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-3">
        <Sk className="h-3 w-28 mb-3" />
        <Sk className="h-4 w-1/2" />
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex justify-between py-1">
            <Sk className="h-4 w-8" />
            <Sk className="h-4 w-24" />
          </div>
        ))}
      </div>
    </div>
  )
}
